import { z } from 'zod';
import { buildingTypeSchema } from './building-type.js';
/**
 * Une personne **telle qu'un formulaire client l'imprime** — donc chaque champ nullable, y compris
 * le nom.
 *
 * ⚠️ Ce n'est pas `personSchema` avec des champs relâchés, et les deux ne doivent pas fusionner :
 * `Person` décrit une **ligne en base** ; celui-ci décrit ce
 * qu'on a **su lire sur un papier**, avant qu'un humain n'ait rien validé. Un bloc absent ou
 * illisible se rend vide plutôt que de faire échouer la lecture (garde-fou n°3) — c'est
 * exactement ce que `personSchema` refuse, et c'est bien qu'il le refuse.
 *
 * ⚠️ **`lastName` et `firstName` sont rendus tels qu'imprimés, jamais recomposés.** Le formulaire
 * réel range l'agence « BLANDINEAU IMMOBILIER » en `Nom` + `Prénom`, alors que M24 § 2 n'a qu'une
 * colonne pour la raison sociale. Deviner qu'il s'agit d'une personne morale pour recoller les
 * deux, c'est décider à la place de l'humain que D7 place au bout de la chaîne.
 */
export const extractedPersonSchema = z.strictObject({
    lastName: z.string().nullable(),
    firstName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
});
/**
 * Le **propriétaire**, seul à rendre son adresse : le rapport papier l'imprime (#350), et ce n'est
 * pas toujours celle du lieu. La commune est rendue verbatim, code postal compris (M1, M32). Le
 * demandeur et le contact n'en rendent pas : rien ne l'imprime, et une donnée sans usage ne se
 * recopie pas (M15).
 */
export const extractedOwnerSchema = extractedPersonSchema.extend({
    address: z.string().nullable(),
    municipality: z.string().nullable(),
});
/**
 * Ce qu'on a su lire d'un PDF de demande de contrôle — **une aide à la saisie, jamais une
 * écriture** ([D7](../../../../docs/ARCHITECTURE.md#d7)). Rien de tout cela n'est persisté : le
 * back-office pré-remplit son formulaire avec, un humain corrige, et c'est `POST /inquiries` qui
 * crée la demande.
 *
 * ⚠️ **Aucun champ de naissance, et c'est une contrainte, pas un oubli.** Le formulaire d'origine
 * porte la date, le pays, le département et la commune de naissance du demandeur payeur ;
 * [M15](../../../../docs/MODELE-DONNEES.md#m15) tranche que rien dans le processus de contrôle
 * n'en a besoin et que l'import **ne doit pas les recopier**. Les ajouter ici les ferait entrer
 * dans le système par la porte de derrière — un test de minimisation garde cette liste
 * (`inquiry-form.parser.spec.ts`).
 *
 * ⚠️ **`reference` n'y est pas non plus**, pour une raison inverse : la « Réf GHP » est notre
 * étiquette, elle n'est imprimée nulle part sur un formulaire client. M15 note qu'elle dérive de
 * *commune + parcelle* — d'où `cadastralReference`, qui donne à l'humain de quoi la composer sans
 * que le serveur ne la lui impose (M1).
 *
 * ⚠️ **L'adresse ne porte pas d'`inseeCode`**, alors que `createInquirySchema` l'exige. Le papier
 * donne un code postal et un nom de commune, et les deux ne suffisent pas : le rapprochement est
 * approximatif (accents, « Saint » / « St », communes déléguées de M8) et se tromper placerait le
 * contrôle dans la mauvaise ville — la première des trois exceptions de M21, l'intégrité
 * structurelle. C'est le sélecteur de commune de l'écran qui tranche, pas une heuristique.
 */
export const extractedInquirySchema = z.strictObject({
    /**
     * Le document a-t-il été **reconnu** comme un formulaire de demande de contrôle.
     *
     * ⚠️ C'est le seul signal que « tout est vide » ne porte pas : un formulaire dont les blocs sont
     * à remplir et un PDF qui n'a aucune couche de texte — un **scan**, le cas que D7 prévoyait —
     * rendent le même objet vide, et ils n'appellent pas la même réponse à l'écran. `false` veut
     * dire « saisissez à la main », jamais « le fichier est mauvais » : aucune des deux situations
     * n'est une erreur (garde-fou n°3).
     */
    recognized: z.boolean(),
    /**
     * « Réf Dossier » (M1) — la référence `CRMI-…` imprimée en tête du document (#585), sinon le
     * numéro que le portail imprime sous le titre.
     */
    caseReference: z.string().nullable(),
    /**
     * Le numéro du portail (« N° 5000001 ») quand la `CRMI-…` a pris sa place dans `caseReference` :
     * l'écran le propose sous le champ, un clic le reprend. `null` s'il fait déjà la référence.
     */
    portalNumber: z.string().nullable(),
    /**
     * Le texte libre du formulaire — le bloc « Introduction : », puis la note imprimée sous la
     * référence `CRMI-…` (#585). Il pré-remplit la note pour l'équipe (`Inquiry.comment`).
     */
    comment: z.string().nullable(),
    /** La parcelle, d'où M15 fait dériver la « Réf GHP ». Rendue, jamais appliquée. */
    cadastralReference: z.string().nullable(),
    /**
     * Le type de bâti (#586) : « Lieu : Maison » du bloc du lieu de contrôle, lu comme la colonne
     * « Type » d'un tableur. Un lieu que la liste ne connaît pas rend `null` : il se choisit à l'écran.
     */
    buildingType: buildingTypeSchema.nullable(),
    /**
     * Le **lieu de contrôle**, pas l'adresse du demandeur — ce sont deux blocs différents.
     * `streetNumber` est le numéro imprimé en tête de la rue, séparé comme à l'import d'une liste
     * (`splitLeadingStreetNumber`, M32) ; `null` quand la rue n'en commence pas par un.
     */
    address: z.strictObject({
        streetNumber: z.string().nullable(),
        street: z.string().nullable(),
        postalCode: z.string().nullable(),
        city: z.string().nullable(),
    }),
    /** Le « demandeur payeur » → `Inquiry.applicant_id` (M15). */
    applicant: extractedPersonSchema,
    /** Le « propriétaire » → `Site.owner_id` (M15). */
    owner: extractedOwnerSchema,
    /** La « personne à contacter pour le rendez-vous » → `Inquiry.appointment_contact_id` (M15). */
    appointmentContact: extractedPersonSchema,
    /**
     * La **date de la demande** imprimée sur le formulaire (#435), `AAAA-MM-JJ`, sans l'heure.
     * ⚠️ Elle s'affiche et s'exporte ; les délais partent de la création (choix du 15/10).
     */
    requestedOn: z.iso.date().nullable(),
});
//# sourceMappingURL=extraction.js.map